import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { filterReportData, formatTime } from './generalFunctions';

export const exportToExcel = async (dataReport, title, startDate, endDate, isRecoveredReports = false) => {

    const data = isRecoveredReports ? dataReport : filterReportData(dataReport, title, startDate, endDate);
// console.log(data)
    const headers = {
        'Kilometraje': ['Kilometraje', 'Tiempo'],
        'Tiempo detenido': ['Tiempo'],
        'Excesos de velocidad': ['Hora de inicio', 'Duración', 'Velocidad media', 'Velocidad máxima'],
        'Ralentí': ['Duración', 'Ralentí', 'Porcentaje en ralentí', 'En movimiento', 'Porcentaje en movimiento', 'Kilometraje', 'Velocidad promedio', 'Intervalos', 'Intervalos promedio'],
        'Combustible': ['Kilometraje por GPS', 'Consumo', 'Consumo por distancia', 'Cant. Inicial', 'Cant. Final', 'Volumen de rellenos', 'Cant. de rellenos', 'Volumen de desagües', 'Cant. de desagües'],
        'Tiempo de trabajo': ['Desde', 'Hasta', 'Tiempo', 'Evento']
    }

    const fields = {
        'Kilometraje': ['mileage', 'time'],
        'Tiempo detenido': ['idle_duration'],
        'Excesos de velocidad': ['start_time', 'duration', 'avg_speed', 'max_speed'],
        'Ralentí': ['duration', 'idle', 'percent_idle', 'in_movement', 'percent_in_movement', 'mileage', 'avg_speed', 'period_count', 'avg_period'],
        'Combustible': ['mileage_by_gps', 'consumed', 'consumption_per_dist', 'start', 'end', 'fillingsVolume', 'fillingsCount', 'drainsVolume', 'drainsCount'],
        'Tiempo de trabajo': ['from', 'to', 'time', 'event']
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(`Reporte de ${title}`);

    // 🎨 Estilos generales
    const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '007bff' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
    };

    const dividerStyle = {
        font: { bold: true },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDDDDD' } }, // Color gris
        alignment: { horizontal: 'center', vertical: 'middle' }
    };

    const dataStyle = {
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
    };

    // 🔹 Extraer fechas y vehículos
    const sortedData = title !== 'Tiempo de trabajo' ? [...data].sort((a, b) => new Date(b.date) - new Date(a.date)) : [...data.trips].sort((a, b) => new Date(b.date) - new Date(a.date)); // Ordenar por fecha DESC
    const sortedData2 = title !== 'Tiempo de trabajo' ? [] : [...data.events].sort((a, b) => new Date(b.date) - new Date(a.date)); // Ordenar por fecha DESC
    const dates = sortedData.map(entry => new Date(entry.date).toLocaleDateString());
    const vehicles = Object.keys(sortedData[0]).filter(key => key !== 'date');

    if (title !== 'Excesos de velocidad' && title !== 'Tiempo de trabajo') {

        // 📌 Encabezados principales (Primera fila: Nombre del vehículo y Fechas)
        const headerRow = ['Nombre del vehículo', ...dates.flatMap(date => headers[title].map(() => date))];
        const headerCells = sheet.addRow(headerRow);
        headerCells.eachCell(cell => { cell.style = headerStyle; });

        // 🔹 Fusionar celdas de fecha en dos columnas
        dates.forEach((_, index) => {
            const colStart = 2 + index * headers[title].length;
            sheet.mergeCells(1, colStart, 1, colStart + headers[title].length - 1);
        });

        const subHeaderRow = [' ', ...dates.flatMap(() => [...headers[title]])];
        const subHeaderCells = sheet.addRow(subHeaderRow);
        subHeaderCells.eachCell(cell => { cell.style = headerStyle; });

        // 🔹 Fusionar "Nombre del vehículo" en dos filas
        sheet.mergeCells(1, 1, 2, 1);

        // 📊 Insertar los datos de cada vehículo
        vehicles.forEach(vehicle => {
            const rowData = [vehicle, ...sortedData.flatMap(entry =>
                fields[title].map(field =>
                    entry?.[vehicle]?.[field] !== undefined
                        ? formatField(field, entry[vehicle][field])
                        : ' - '
                )
            )];
            const row = sheet.addRow(rowData);
            row.eachCell(cell => { cell.style = dataStyle; });
        });

        // 🛠️ Inmovilizar la primera columna
        sheet.views = [{ state: 'frozen', xSplit: 1 }];

    } else {
        if (title === 'Excesos de velocidad') {
            let colOffset = 1;

            vehicles.forEach(vehicle => {
                let colStart = colOffset;

                // 📌 Agregar nombre del vehículo (primera fila)
                sheet.getRow(1).getCell(colStart).value = vehicle;
                sheet.mergeCells(1, colStart, 1, colStart + headers[title].length - 1);
                sheet.getRow(1).getCell(colStart).style = headerStyle;

                // 📌 Agregar encabezados (segunda fila)
                headers[title].forEach((header, index) => {
                    sheet.getRow(2).getCell(colStart + index).value = header;
                    sheet.getRow(2).getCell(colStart + index).style = headerStyle;
                });

                let rowIdx = 3; // Primera fila de datos

                // 📊 Insertar los datos por fecha con separadores
                sortedData.forEach(entry => {
                    if (entry[vehicle] && entry[vehicle].length > 0) {
                        // 📌 Agregar fila divisora con la fecha
                        sheet.getRow(rowIdx).getCell(colStart).value = new Date(entry.date).toLocaleDateString();
                        sheet.mergeCells(rowIdx, colStart, rowIdx, colStart + headers[title].length - 1);
                        sheet.getRow(rowIdx).getCell(colStart).style = dividerStyle;
                        rowIdx++;

                        entry[vehicle].forEach(speedEvent => {
                            sheet.getRow(rowIdx).getCell(colStart).value = new Date(speedEvent.start_time).toLocaleTimeString();
                            sheet.getRow(rowIdx).getCell(colStart + 1).value = formatTime(speedEvent.duration);
                            sheet.getRow(rowIdx).getCell(colStart + 2).value = `${speedEvent.avg_speed} km/h`;
                            sheet.getRow(rowIdx).getCell(colStart + 3).value = `${speedEvent.max_speed} km/h`;

                            // Aplicar estilo a cada celda
                            for (let i = 0; i < headers[title].length; i++) {
                                sheet.getRow(rowIdx).getCell(colStart + i).style = dataStyle;
                            }

                            rowIdx++;
                        });

                        // 🔹 Agregar una fila vacía entre fechas para mejor separación visual
                        rowIdx++;
                    }
                });

                // 📏 Ajustar ancho de columnas automáticamente
                for (let i = 0; i < headers[title].length; i++) {
                    sheet.getColumn(colStart + i).width = 15;
                }

                // 🔹 Separación entre vehículos
                colOffset += headers[title].length + 1;

                // 🛠️ Inmovilizar las primeras dos filas
                sheet.views = [{ state: 'frozen', ySplit: 2 }];
            });
        } else {
            let colOffset = 1;

            vehicles.forEach(vehicle => {
                let colStart = colOffset;

                // 📌 Agregar nombre del vehículo (primera fila)
                sheet.getRow(1).getCell(colStart).value = vehicle;
                sheet.mergeCells(1, colStart, 1, colStart + 3);
                sheet.getRow(1).getCell(colStart).style = headerStyle;
                
                // 📌 Agregar encabezados (segunda fila)
                sheet.getRow(2).getCell(colStart).value = 'Por viajes';
                sheet.mergeCells(2, colStart, 2, colStart + 1);
                sheet.getRow(2).getCell(colStart).style = headerStyle;

                sheet.getRow(2).getCell(colStart + 2).value = 'Por ignición';
                sheet.mergeCells(2, colStart + 2, 2, colStart + 3);
                sheet.getRow(2).getCell(colStart + 2).style = headerStyle;
                
                // 📌 Agregar encabezados (tercera fila)
                headers[title].forEach((header, index) => {
                    sheet.getRow(3).getCell(colStart + index).value = header;
                    sheet.getRow(3).getCell(colStart + index).style = headerStyle;
                });

                let rowIdx = 4; // Primera fila de datos

                dates.forEach(date => {
                    const tripsData = sortedData.find(entry => new Date(entry.date).toLocaleDateString() === date) || [];
                    const eventsData = sortedData2.find(entry => new Date(entry.date).toLocaleDateString() === date) || [];
                    
                    const tripsDataCount = tripsData[vehicle]?.length || 0;
                    const eventsDataCount = eventsData[vehicle]?.length || 0;
                    if (tripsDataCount === 0 && eventsDataCount === 0) return;
                    
                    sheet.getRow(rowIdx).getCell(colStart).value = date;
                    sheet.mergeCells(rowIdx, colStart, rowIdx, colStart + headers[title].length - 1);
                    sheet.getRow(rowIdx).getCell(colStart).style = dividerStyle;
                    rowIdx++;

                    const firstRow = rowIdx;

                    const lastRow = tripsDataCount > eventsDataCount ? rowIdx + tripsDataCount : rowIdx + eventsDataCount;

                    tripsData[vehicle].forEach(entry => {
                        sheet.getRow(rowIdx).getCell(colStart).value = new Date(entry.from).toLocaleTimeString();
                        sheet.getRow(rowIdx).getCell(colStart + 1).value = new Date(entry.to).toLocaleTimeString();

                        rowIdx++;
                    });

                    rowIdx = firstRow;

                    if (eventsData[vehicle]) {
                        eventsData[vehicle].forEach(entry => {
                            sheet.getRow(rowIdx).getCell(colStart + 2).value = new Date(entry.time).toLocaleTimeString();
                            sheet.getRow(rowIdx).getCell(colStart + 3).value = entry.event;

                            rowIdx++;
                        });
                    }

                    rowIdx = lastRow + 1;
                });

                // 📊 Insertar los datos por fecha con separadores
                // sortedData.forEach(entry => {
                //     if (entry[vehicle] && entry[vehicle].length > 0) {
                //         // 📌 Agregar fila divisora con la fecha
                //         sheet.getRow(rowIdx).getCell(colStart).value = new Date(entry.date).toLocaleDateString();
                //         sheet.mergeCells(rowIdx, colStart, rowIdx, colStart + headers[title].length - 1);
                //         sheet.getRow(rowIdx).getCell(colStart).style = dividerStyle;
                //         rowIdx++;

                //         const eventsData = sortedData2.find(obj => obj.date === entry.date) || [];

                //         entry[vehicle].forEach(speedEvent => {
                //             const vehicleEventsData = eventsData[vehicle] || [];

                //             sheet.getRow(rowIdx).getCell(colStart).value = new Date(speedEvent.from).toLocaleTimeString();
                //             sheet.getRow(rowIdx).getCell(colStart + 1).value = new Date(speedEvent.to).toLocaleTimeString();
                //             sheet.getRow(rowIdx).getCell(colStart + 2).value = `${vehicleEventsData.time} km/h`;
                //             sheet.getRow(rowIdx).getCell(colStart + 3).value = `${vehicleEventsData.event} km/h`;

                //             // Aplicar estilo a cada celda
                //             for (let i = 0; i < headers[title].length; i++) {
                //                 sheet.getRow(rowIdx).getCell(colStart + i).style = dataStyle;
                //             }

                //             rowIdx++;
                //         });

                //         // 🔹 Agregar una fila vacía entre fechas para mejor separación visual
                //         rowIdx++;
                //     }
                // });

                // sortedData.forEach(entry => {
                //     if (entry[vehicle] && entry[vehicle].length > 0) {
                //         sheet.addRow([entry.date]);
                //         vehicles.forEach(vehicle => {
                //             if (entry[vehicle]) {
                //                 entry[vehicle].forEach(trip => {
                //                     sheet.addRow(['', new Date(trip.from).toLocaleTimeString(), new Date(trip.to).toLocaleTimeString()]);
                //                 });
                //             }
                //         });

                //         const eventEntry = sortedData2.find(e => e.date === entry.date) || {};
                //         vehicles.forEach(vehicle => {
                //             if (eventEntry[vehicle]) {
                //                 eventEntry[vehicle].forEach(event => {
                //                     sheet.addRow(['', '', '', new Date(event.time).toLocaleTimeString(), event.event]);
                //                 });
                //             }
                //         });
                //     }
                // });

                // 📏 Ajustar ancho de columnas automáticamente
                for (let i = 0; i < headers[title].length; i++) {
                    sheet.getColumn(colStart + i).width = 15;
                }

                // 🔹 Separación entre vehículos
                colOffset += headers[title].length + 1;

                // 🛠️ Inmovilizar las primeras dos filas
                sheet.views = [{ state: 'frozen', ySplit: 3 }];
            });
        }
    }

    // 📏 Ajustar el ancho de las columnas automáticamente
    sheet.columns.forEach(column => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, cell => {
            const cellValue = cell.value ? cell.value.toString() : "";
            maxLength = Math.max(maxLength, cellValue.length);
        });
        column.width = maxLength + 2; // Agregar un poco de espacio extra
    });

    // 🔽 Generar y descargar el archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Reporte de ${title}.xlsx`);
};

const formatField = (field, value) => {
    if (['time', 'idle_duration', 'duration', 'idle', 'in_movement', 'avg_period'].includes(field)) {
        return formatTime(value); // Formatear duración en "hh:mm:ss"
    }
    if (['percent_idle', 'percent_in_movement'].includes(field)) {
        return `${value} %`; // Formatear como porcentaje
    }
    if (['mileage', 'mileage_by_gps'].includes(field)) {
        return `${value.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`; // Formatear como "km"
    }
    if (['avg_speed'].includes(field)) {
        return `${value} km/h`; // Formatear como "km/h"
    }
    if (['consumed', 'start', 'end', 'fillingsVolume', 'fillingsCount', 'drainsVolume', 'drainsCount'].includes(field)) {
        return `${value} L`; // Formatear como "L"
    }
    if (['consumption_per_dist'].includes(field)) {
        return `${value} L/km`; // Formatear como "L"
    }
    return value;
};